export { TursoOwnershipError } from './worker-error'
export type { ReplicaResponse, ReplicaResponsePart } from '@unni-craftsman/turso-fts-common/dist/replication'
export { readReplicaState } from './replica-state'
export type { FailureScope, ReplicaFailures, FailureSummary, ItemSource, ItemFailureCode, OwnerCardDerivation, ReceivedContent, SourceStatement, SourceValue } from '@unni-craftsman/turso-fts-common'
import { createFailureClient, executeFailureCommand, type FailureRequest, type FailureEvent } from './failure-commands'
import type { FailureScope, SourceStatement } from '@unni-craftsman/turso-fts-common'
import type { SourceTransaction } from '../source-transaction'
export type { SourceTransaction, SourceTransactionContext } from '../source-transaction'
import { copySourceBatch, validateSourceCallContext, deliverSourceCompletion } from './source-commands'
import { runTransactionAttempts } from '../../../db/src/db'
import type { TursoDriverCallContext } from '../database-adapter'
import { createWorkerSqlQueue } from './turso-worker-driver'
export { createReplicaTransport, ReplicaTransportError } from './replica-transport'
import { copyReplicaTransport, replicaTransportKey, type ReplicaTransport, type ReplicaTransportConfig } from './replica-transport'
// INV-6: failed physical hosts retire before reuse; pending work never replays into their replacement.
import * as Comlink from 'comlink'
import { getTursoWasm, preparePageWasm, receiveTursoWasm } from './turso-wasm-module'
import { disposeAll } from '@w/utils'
import { createOwnedReplicaTransport } from './injected-replica-transport'
import { serializeWorkerError as serializeError } from './turso-worker-driver'
import { runtimeMonitor } from '@w/runtime'
import type { DatabaseDriver, DriverCallContext } from '@w/db'
import { nullTel, type Tel } from '@w/tel'
import type { Migration } from '@w/db/migrate/browser'
import type {
  TursoWorkerBootMetrics,
  TursoWorkerDriver,
  WorkerCallBaggage,
  WorkerCallError,
  WorkerCallEnvelope,
  WorkerReplicaEvent,
} from './turso-worker-driver'
import { registerDevDatabase } from '../dev-db-registry'
export { registerDevDatabase } from '../dev-db-registry'
import { createSharedWorkerClient } from './opfs-owner-client'
import {
  createTursoWorkerProxy,
  createWorkerTransactions,
  deserializeWorkerError,
  type TursoReplicaState,
  type TursoWorkerDatabaseDriver,
} from './turso-worker-proxy'

export type { TursoReplicaState, TursoWorkerDatabaseDriver, TursoWorkerOwnership } from './turso-worker-proxy'
export { TursoWorkerDriver } from './turso-worker-driver'
export type {
  TursoWorkerTaskContext,
  TursoWorkerTaskModule,
  TursoWorkerTaskRegistry,
} from './turso-worker-driver'

export type TursoWorkerDriverOptions = {
  replicaTransport?: ReplicaTransport
  replica?: ReplicaTransportConfig
  capture?: 'full'
  onInvalidated?: (ids: number[]) => void
  env?: string
  path?: string
  migrations?: Migration[]
  createSharedWorker?: (url: string, options: WorkerOptions) => SharedWorker
  /** Dedicated engine factory for the local :memory: driver. */
  createWorker?: (name: string) => Worker
  tel: Tel
  onReplicaEvent?: (event: WorkerReplicaEvent) => void
}

type SharedTursoWorkerDriver = {
  replicaKey: string
  replicaTransport?: ReplicaTransport
  driver: TursoWorkerDatabaseDriver
  destroyed: boolean
  refs: number
  unsubscribeDestroy: () => void
}

type SharedRpcOp =
  | 'sourceTransaction'
  | 'failureCommand'
  | 'subscribeFailures'
  | 'unsubscribeFailures'
  | 'cancelExchange'
  | 'exchange'
  | 'resetReplica'
  | 'setSessionUser'
  | 'subscribe'
  | 'unsubscribe'
  | 'beginTx'
  | 'close'
  | 'commitTx'
  | 'deleteByPrefix'
  | 'disconnect'
  | 'exec'
  | 'open'
  | 'ping'
  | 'query'
  | 'rollbackTx'
  | 'runTask'
  | 'txExec'
  | 'txQuery'

export type SharedRpcRequest = {
  replica?: ReplicaTransportConfig
  capture?: 'full'
  args?: unknown[]
  migrations?: Migration[]
  op: SharedRpcOp
  path?: string
  requestId: number
  injected?: boolean
  sessionId: string
  type: 'request'
}

export type SharedRpcResponse = {
  error?: WorkerCallError
  requestId: number
  result?: unknown
  type: 'response'
}

export type HostRpcRequest = Omit<SharedRpcRequest, 'type'> & {
  hostRequestId: number
  type: 'host-request'
}

export type SharedWorkerClient = {
  failureEvents: Set<(event: FailureEvent) => void>
  subscribeReplica(listener: (state: TursoReplicaState) => void): Promise<() => void>
  subscribe(sql: string, onInvalidate: () => void): Promise<() => void>
  close(): Promise<void>
  request<T>(request: Omit<SharedRpcRequest, 'requestId' | 'type'>): Promise<T>
}

export type LocalHostDriver = {
  replicaKey: string
  failureSubscriptions: Map<string, Set<string>>
  txSessionId?: string
  driver: ReturnType<typeof createLocalTursoWorkerDriver>
  invalidationTarget: { port: MessagePort }
  subscriptions: Map<string, Map<number, number>>
  release: () => Promise<void>
}

export type ResolvedTursoWorkerDriverOptions = {
  replicaTransport?: ReplicaTransport
  replica?: ReplicaTransportConfig
  capture?: 'full'
  onOwnershipChange?: (owned: boolean) => void
  createSharedWorker?: (url: string, options: WorkerOptions) => SharedWorker
  createWorker?: (name: string) => Worker
  env?: string
  migrations: Migration[]
  path: string
  tel: Tel
}

export type TursoWorkerDatabaseDestroyEvent = {
  prefix?: string
}

export type TursoWorkerDatabaseDestroyListener = (
  event: TursoWorkerDatabaseDestroyEvent,
) => Promise<void> | void

const sharedDrivers = new Map<string, SharedTursoWorkerDriver>()
const destroyListeners = new Set<TursoWorkerDatabaseDestroyListener>()

export function createTursoWorkerDriver(
  options: TursoWorkerDriverOptions,
): TursoWorkerDatabaseDriver {
  options = { ...options, replica: copyReplicaTransport(options.replica) }
  const path = options.path ?? ':memory:'
  const tel = options.tel
  if (path === ':memory:')
    return registerDevDatabase(path, instrumentDriver(path, createLocalTursoWorkerDriver(options)))

  const shared = sharedDrivers.get(path)
  if (shared) {
    if (shared.replicaKey !== replicaTransportKey(options.replica) || shared.replicaTransport !== options.replicaTransport) throw new Error('Database already opened with different replication configuration')
    shared.refs += 1
    return registerDevDatabase(
      path,
      instrumentDriver(path, createSharedDriverRef(path, shared, tel)),
    )
  }

  const driver = createSharedTursoWorkerDriver({
    replicaTransport: options.replicaTransport,
    createSharedWorker: options.createSharedWorker,
    createWorker: options.createWorker,
    env: options.env,
    path,
    replica: copyReplicaTransport(options.replica),
    capture: options.capture,
    migrations: options.migrations ?? [],
    tel: options.tel,
  })
  const next: SharedTursoWorkerDriver = {
    replicaKey: replicaTransportKey(options.replica),
    replicaTransport: options.replicaTransport,
    destroyed: false,
    driver,
    refs: 1,
    unsubscribeDestroy: () => {},
  }
  next.unsubscribeDestroy = subscribeTursoWorkerDatabaseDestroy(
    async (event) => {
      if (event.prefix && !path.startsWith(event.prefix)) return
      if (next.destroyed) return
      next.destroyed = true
      sharedDrivers.delete(path)
      next.unsubscribeDestroy()
      await next.driver.close?.()
    },
  )
  sharedDrivers.set(path, next)
  return registerDevDatabase(
    path,
    instrumentDriver(path, createSharedDriverRef(path, next, tel)),
  )
}

/** Capture calls before worker boot, transaction and broker waits. */
function instrumentDriver(
  path: string,
  driver: TursoWorkerDatabaseDriver,
): TursoWorkerDatabaseDriver {
  const monitored = new Set<PropertyKey>(['query', 'exec', 'exchange', 'transaction', 'runWorkerTask', 'subscribe', 'close'])
  return new Proxy(driver, {
    get(target, property, receiver) {
      const value: unknown = Reflect.get(target, property, receiver)
      if (typeof value !== 'function') return value
      if (!monitored.has(property)) return value
      return (...args: unknown[]) => runtimeMonitor.run(
        { name: `Database ${String(property)}`, kind: 'database', detail: path },
        () => Promise.resolve(Reflect.apply(value, target, args)),
      )
    },
  })
}

export function subscribeTursoWorkerDatabaseDestroy(
  listener: TursoWorkerDatabaseDestroyListener,
): () => void {
  destroyListeners.add(listener)
  return () => {
    destroyListeners.delete(listener)
  }
}

export async function destroyTursoWorkerDatabases(
  event: TursoWorkerDatabaseDestroyEvent = {},
): Promise<void> {
  await Promise.all(
    [...destroyListeners].map((listener) => Promise.resolve(listener(event))),
  )
}

export async function clearTursoWorkerDatabasesByPrefix(
  prefix: string,
): Promise<void> {
  if (!prefix) throw new Error('Turso database prefix is required')

  await destroyTursoWorkerDatabases({ prefix })

  const worker = new Worker(new URL('./turso-worker.ts', import.meta.url), {
    type: 'module',
    name: `clear-${prefix}`,
  })
  const remote = Comlink.wrap<TursoWorkerDriver>(worker)
  const bootFailure = watchTursoWorkerBootFailure(worker, `clear-${prefix}`)
  try {
    await raceTursoWorkerBootFailure(
      remote.deleteDatabasesByPrefix(prefix),
      bootFailure,
    )
  } finally {
    bootFailure.dispose()
    worker.terminate()
  }
}

/**
 * Listens for a worker's `error`/`messageerror` events and exposes a promise
 * that rejects as soon as either fires, so callers can fail fast instead of
 * waiting on an RPC that will never resolve (e.g. the dev-server-served
 * `text/html` instead of the worker's `text/javascript` asset — see
 * apps/platform/dev/src/bundled-dev-turso-worker-anchor.ts). Call `dispose()`
 * once the caller's own promise has settled to avoid leaking listeners.
 */
function watchTursoWorkerBootFailure(
  worker: Worker,
  label: string,
): { dispose: () => void; promise: Promise<never> } {
  let handleError: (event: ErrorEvent) => void = () => {}
  let handleMessageError: (event: MessageEvent) => void = () => {}
  const promise = new Promise<never>((_resolve, reject) => {
    handleError = (event: ErrorEvent) => {
      reject(
        new Error(
          `Turso worker failed to start (${label}): ${event.message || 'unknown worker error'}`,
        ),
      )
    }
    handleMessageError = () => {
      reject(
        new Error(
          `Turso worker failed to start (${label}): received a message it could not deserialize`,
        ),
      )
    }
    worker.addEventListener('error', handleError)
    worker.addEventListener('messageerror', handleMessageError)
  })
  void promise.catch(() => {})
  return {
    dispose() {
      worker.removeEventListener('error', handleError)
      worker.removeEventListener('messageerror', handleMessageError)
    },
    promise,
  }
}

function raceTursoWorkerBootFailure<T>(
  work: Promise<T>,
  bootFailure: { promise: Promise<never> },
): Promise<T> {
  return Promise.race([work, bootFailure.promise])
}

// Ordinary and replica hosts share one compiled page module and preparation.
function createLocalTursoWorkerDriver(
  options: TursoWorkerDriverOptions,
  onFailure?: (error: Error) => void,
): ReturnType<typeof createTursoWorkerProxy> & { _callWorker(op: string, args: unknown[]): Promise<unknown> } {
  const path = options.path ?? ':memory:'
  const ownedTransport = options.replicaTransport && createOwnedReplicaTransport(options.replicaTransport, options.tel)
  let destroyed = false
  let closed = false
  let closing: Promise<void> | undefined
  let configured = false
  let workerFailure: Error | undefined
  const pendingCalls = new Set<(error: Error) => void>()
  const worker =
    options.createWorker?.(path) ??
    new Worker(new URL('./turso-worker.ts', import.meta.url), {
      type: 'module',
      name: path,
    })
  // Comlink does not reject RPCs when its worker dies. Guard every method,
  // including host RPCs and subscriptions, and forget each settled call.
  const remote = new Proxy(Comlink.wrap<TursoWorkerDriver>(worker), {
    get(target, property, receiver) {
      const method: unknown = Reflect.get(target, property, receiver)
      if (typeof method !== 'function') return method
      return (...args: unknown[]) => {
        if (workerFailure) return Promise.reject(workerFailure)
        return new Promise((resolve, reject) => {
          pendingCalls.add(reject)
          void Promise.resolve().then(() => {
            if (workerFailure) throw workerFailure
            return Reflect.apply(method, target, args) as unknown
          }).then(
            result => { pendingCalls.delete(reject); resolve(result); return null },
            (error: unknown) => { pendingCalls.delete(reject); reject(error instanceof Error ? error : new Error(String(error))); return null },
          )
        })
      }
    },
  })
  const runtimeWorker = runtimeMonitor.observeWorker(worker, { name: `Database: ${path}`, kind: 'dedicated' })
  function failWorker(error: Error): void {
    if (workerFailure) return
    workerFailure = error
    for (const reject of pendingCalls) reject(error)
    pendingCalls.clear()
    if (configured && !closed) onFailure?.(error)
  }
  const runtimeError = (event: ErrorEvent) => {
    event.preventDefault()
    const error = new Error(`Turso worker ${configured ? 'failed' : 'failed to start'} (${path}): ${event.message || 'unknown worker error'}`, { cause: event.error })
    runtimeWorker.fail(error)
    failWorker(error)
  }
  const runtimeMessageError = () => {
    const error = new Error(`Turso worker ${configured ? 'failed' : 'failed to start'} (${path}): received a message it could not deserialize`)
    runtimeWorker.fail(error)
    failWorker(error)
  }
  worker.addEventListener('error', runtimeError)
  worker.addEventListener('messageerror', runtimeMessageError)
  const bootRemote = async (): Promise<TursoWorkerBootMetrics | undefined> => {
    // Proxy construction posts subscribeReplica before configure. Both RPCs
    // share one ordered endpoint; configuration needn't wait for its reply.
    // Keep boot readiness gated on both, including initial state delivery.
    await Promise.resolve()
    const configuring = remote.configure({
      wasm: getTursoWasm(),
      env: options.env,
      path,
      replica: copyReplicaTransport(options.replica),
      capture: options.capture,
      parentBaggage: options.tel === nullTel ? null : options.tel._getBaggage(),
      migrations: options.migrations ?? [],
    }, ownedTransport && Comlink.proxy(ownedTransport.remote))
    const [boot] = await Promise.all([configuring, driver._replicaReady])
    if (boot.wasm) receiveTursoWasm(boot.wasm)
    return boot.metrics
  }
  const configurePromise = (
    options.tel.span(
          {
            name: 'db.worker.boot',
            target: 'db',
            type: 'mutation',
            input: { path },
          },
          async (spanTel) => {
            const metrics = await bootRemote()
            if (metrics)
              spanTel.currentSpan.update({ attributes: { ...metrics } })
            return metrics
          },
        )
  )
    .then(metrics => { configured = true; return metrics })
    .catch((error: unknown) => {
      throw annotateTursoWorkerError(path, error)
    })
  void configurePromise.catch(() => {})
  const bootMetrics = swallowBootFailure(configurePromise)
  const driver = createTursoWorkerProxy(remote, {
    concurrentTransactions: !!options.replica,
    tel: options.tel,
    onInvalidated: options.onInvalidated,
    onReplicaEvent: options.onReplicaEvent,
  }, configurePromise)
  const unsubscribeDestroy = subscribeTursoWorkerDatabaseDestroy(
    async (event) => {
      if (event.prefix && !path.startsWith(event.prefix)) return
      destroyed = true
      await closeWorker()
    },
  )

  function closeWorker(): Promise<void> {
    if (closing) return closing
    closed = true
    ownedTransport?.abort()
    unsubscribeDestroy()
    if (!configured) failWorker(new Error(`Turso worker database closed during startup: ${path}`))
    closing = (async () => {
      try {
        // Always clear proxy subscriptions, even when the guarded remote will
        // reject close immediately because its worker has already failed.
        await driver.close?.().catch((error: unknown) => {
          // A crash during close makes termination the only possible cleanup.
          let cleanupError = error
          while (cleanupError instanceof SuppressedError && cleanupError.error === workerFailure) cleanupError = cleanupError.suppressed
          if (cleanupError !== workerFailure) throw error
        })
      } finally {
        await ownedTransport?.join()
        runtimeWorker.close()
        worker.removeEventListener('error', runtimeError)
        worker.removeEventListener('messageerror', runtimeMessageError)
        failWorker(new Error(`Turso worker database closed: ${path}`))
        worker.terminate()
      }
    })()
    return closing
  }

  function assertNotDestroyed(): void {
    if (destroyed) throw new Error(`Turso worker database destroyed: ${path}`)
    if (closed) throw new Error(`Turso worker database closed: ${path}`)
  }

  return {
    ...driver,
    get _replicaState() { return driver._replicaState },
    exchange(ctx?: DriverCallContext & { signal?: AbortSignal }) {
      assertNotDestroyed()
      if (!driver.exchange) return Promise.reject(new Error('Turso driver missing native replica'))
      return driver.exchange(ctx)
    },
    bootMetrics,
    subscribeReplica(listener) {
      assertNotDestroyed()
      if (!driver.subscribeReplica) return Promise.reject(new Error('Turso driver missing native replica'))
      return driver.subscribeReplica(listener)
    },
    _callWorker(op: string, args: unknown[]): Promise<unknown> {
      assertNotDestroyed()
      if (op === 'sourceTransaction') return remote.sourceTransaction(args[0] as FailureScope, args[1] as readonly SourceStatement[], args[2] as WorkerCallBaggage | null, args[3] as boolean | undefined)
      if (op === 'failureCommand') return remote.failureCommand(args[0] as FailureRequest)
      if (op === 'subscribeFailures') return remote.subscribeFailures(args[0] as FailureScope, args[1] as string)
      if (op === 'unsubscribeFailures') return remote.unsubscribeFailures(args[0] as string)
      if (op === 'resetReplica') return remote.resetReplica(args[0] as Uint8Array, args[1] as WorkerCallBaggage | null)
      if (op === 'setSessionUser') return remote.setSessionUser(args[0] as string | null)
      if (op === 'exchange') return remote.exchange(args[0] as WorkerCallBaggage | null,args[1] as string | undefined)
      if (op === 'cancelExchange') return Promise.resolve(remote.cancelExchange(args[0] as string))
      if (op === 'query')
        return remote.query(
          args[0] as string,
          args[1] as unknown[],
          args[2] as WorkerCallBaggage | null,
        )
      if (op === 'exec')
        return remote.exec(
          args[0] as string,
          args[1] as unknown[],
          args[2] as WorkerCallBaggage | null,
        )
      if (op === 'txQuery')
        return remote.txQuery(
          args[0] as string,
          args[1] as unknown[],
          args[2] as WorkerCallBaggage | null,
        )
      if (op === 'txExec')
        return remote.txExec(
          args[0] as string,
          args[1] as unknown[],
          args[2] as WorkerCallBaggage | null,
        )
      if (op === 'runTask')
        return remote.runTask(
          args[0] as string,
          args[1],
          args[2] as WorkerCallBaggage | null,
        )
      if (op === 'beginTx') return remote.beginTxWithEnvelope(args[0] as { exclusive?: boolean } | undefined)
      if (op === 'commitTx') return remote.commitTx()
      if (op === 'rollbackTx') return remote.rollbackTx()
      throw new Error(`Unknown Turso worker operation: ${op}`)
    },
    async beginTx(context) {
      assertNotDestroyed()
      await configurePromise
      const envelope = await remote.beginTxWithEnvelope(context)
      if (!envelope.ok) throw deserializeWorkerError(envelope.error)
    },
    async close() {
      await closeWorker()
    },
    async commitTx() {
      assertNotDestroyed()
      await configurePromise
      await driver.commitTx()
    },
    async rollbackTx() {
      assertNotDestroyed()
      await configurePromise
      await driver.rollbackTx()
    },
    async txExec(sql: string, params?: unknown[], ctx?: DriverCallContext) {
      assertNotDestroyed()
      await driver.txExec?.(sql, params, ctx)
    },
    txQuery<T>(sql: string, params?: unknown[], ctx?: DriverCallContext) {
      assertNotDestroyed()
      if (!driver.txQuery) throw new Error('Turso worker txQuery unavailable')
      return driver.txQuery<T>(sql, params, ctx)
    },
    query<T>(
      sql: string,
      params?: unknown[],
      ctx?: TursoDriverCallContext,
    ): Promise<T[]> {
      assertNotDestroyed()
      return driver.query<T>(sql, params, ctx)
    },
    exec(
      sql: string,
      params?: unknown[],
      ctx?: TursoDriverCallContext,
    ): Promise<void> {
      assertNotDestroyed()
      return driver.exec(sql, params, ctx)
    },
    transaction<T>(
      fn: (txArg: DatabaseDriver) => Promise<T>,
      ctx?: TursoDriverCallContext,
    ): Promise<T> {
      assertNotDestroyed()
      return driver.transaction(fn, ctx)
    },
    runWorkerTask<TOutput>(
      moduleUrl: string,
      input: unknown,
      ctx?: TursoDriverCallContext,
    ): Promise<TOutput> {
      assertNotDestroyed()
      return driver.runWorkerTask<TOutput>(moduleUrl, input, ctx)
    },
  }
}

function createSharedTursoWorkerDriver(
  options: ResolvedTursoWorkerDriverOptions,
): TursoWorkerDatabaseDriver {
  const defaultTel = options.tel
  const sessionId = crypto.randomUUID()
  const openDatabase = async (
    client: SharedWorkerClient,
  ): Promise<TursoWorkerBootMetrics | null> => {
    const metrics = await client.request<TursoWorkerBootMetrics | null>({
      replica: copyReplicaTransport(options.replica),
      injected: Boolean(options.replicaTransport),
      capture: options.capture,
      migrations: options.migrations,
      op: 'open',
      path: options.path,
      sessionId,
    })
    await preparePageWasm()
    return metrics
  }
  let resolveBootMetrics!: (metrics?: TursoWorkerBootMetrics) => void
  const bootMetrics = new Promise<TursoWorkerBootMetrics | undefined>(
    (resolve) => {
      resolveBootMetrics = resolve
    },
  )
  let owned = false
  const ownershipListeners = new Set<(owned: boolean) => void>()
  const ownership = { isOwner: () => owned, subscribe(listener: (owned: boolean) => void) {
    ownershipListeners.add(listener)
    return () => { ownershipListeners.delete(listener) }
  } }
  const sharedClient = createSharedWorkerClient(sessionId, { ...options, onOwnershipChange(value) {
    if (owned === value) return
    owned = value
    for (const listener of ownershipListeners) listener(value)
  } })
  let opening: Promise<SharedWorkerClient> | undefined
  function getClient(): Promise<SharedWorkerClient> {
    if (opening) return opening
    opening = Promise.resolve(sharedClient).then(
      async (client) => {
        const metrics = await options.tel.span(
              {
                name: 'db.worker.boot',
                target: 'db',
                type: 'mutation',
                input: { path: options.path, shared: true },
              },
              async (spanTel) => {
                const hostMetrics = await openDatabase(client)
                if (hostMetrics)
                  spanTel.currentSpan.update({ attributes: { ...hostMetrics } })
                return hostMetrics
              },
            )
        if (metrics) resolveBootMetrics(metrics)
        else resolveBootMetrics()
        return client
      },
    )
    opening = opening.catch((error: unknown) => {
      opening = undefined
      resolveBootMetrics()
      throw error
    })
    return opening
  }
  void getClient().catch(() => {})

  const runAfterTransactions = createWorkerSqlQueue()
  function callBaggage(tel: Tel): WorkerCallBaggage | null {
    if (tel === nullTel) return null
    const baggage = tel._getBaggage()
    return {
      baggage: baggage.baggage,
      parentId: baggage.parentId,
      sessionId: baggage.sessionId,
      traceFlags: baggage.traceFlags,
      traceId: baggage.traceId,
      traceState: baggage.traceState,
    }
  }

  function unwrap<T>(envelope: WorkerCallEnvelope<T>, tel: Tel): T {
    if (envelope.telemetry && tel !== nullTel) {
      try { tel._insertBoundaryTrace(
        envelope.telemetry.trace,
        envelope.telemetry.metrics,
        envelope.telemetry.resource,
      ) } catch { /* Optional import cannot replace the database result or original error. */ }
    }
    if (envelope.ok) return envelope.result
    throw deserializeWorkerError(envelope.error)
  }

  const tx: Omit<DatabaseDriver, 'transaction'> = {
    dialect: 'sqlite',
    query<T>(
      sql: string,
      params?: unknown[],
      ctx?: TursoDriverCallContext,
    ): Promise<T[]> {
      const tel = ctx?.tel ?? defaultTel
      return getClient().then((client) =>
        client
          .request<WorkerCallEnvelope<T[]>>({
            args: [sql, params, callBaggage(tel)],
            op: 'txQuery',
            sessionId,
          })
          .then((envelope) => unwrap(envelope, tel)),
      )
    },
    async exec(
      sql: string,
      params?: unknown[],
      ctx?: TursoDriverCallContext,
    ): Promise<void> {
      const tel = ctx?.tel ?? defaultTel
      const client = await getClient()
      const envelope = await client.request<WorkerCallEnvelope<void>>({
        args: [sql, params, callBaggage(tel)],
        op: 'txExec',
        sessionId,
      })
      unwrap(envelope, tel)
    },
    copyFrom(): Promise<void> {
      return Promise.reject(new Error('Turso driver does not support copyFrom'))
    },
  }
  const { runRootTransactionNow } = createWorkerTransactions({
    tx,
    async begin(ctx) {
      const client = await getClient()
      unwrap(await client.request<WorkerCallEnvelope<void>>({ op: 'beginTx', sessionId, args: [{ exclusive: ctx?.exclusive }] }), ctx?.tel ?? defaultTel)
    },
    async commit(tel) { const client = await getClient(); unwrap(await client.request<WorkerCallEnvelope<void>>({ op: 'commitTx', sessionId }), tel) },
    async rollback(tel) { const client = await getClient(); unwrap(await client.request<WorkerCallEnvelope<void>>({ op: 'rollbackTx', sessionId }), tel) },
  }, defaultTel, !!options.replica)

  const failures = createFailureClient(
    request => runAfterTransactions(async () => { const client = await getClient(); return unwrap(await client.request<WorkerCallEnvelope<unknown>>({ op: 'failureCommand', sessionId, args: [request] }), defaultTel) }),
    (scope, id) => runAfterTransactions(async () => { const client = await getClient(); unwrap(await client.request<WorkerCallEnvelope<void>>({ op: 'subscribeFailures', sessionId, args: [scope, id] }), defaultTel) }),
    async id => { const client = await getClient(); unwrap(await client.request<WorkerCallEnvelope<void>>({ op: 'unsubscribeFailures', sessionId, args: [id] }), defaultTel) },
    sharedClient.failureEvents,
  )
  return {
    failures,
    sourceTransaction: (scope, statements, context) => {
      const batch = copySourceBatch(scope, statements)
      const owned = { ...context }
      validateSourceCallContext(owned)
      const tel = owned.tel ?? defaultTel
      return runAfterTransactions(async () => {
        const client = await getClient()
        const envelope = await client.request<WorkerCallEnvelope<Awaited<ReturnType<SourceTransaction>>>>({ op: 'sourceTransaction', sessionId, args: [batch.scope, batch.statements, callBaggage(tel), owned.exclusive] })
        deliverSourceCompletion(envelope.source, owned)
        return unwrap(envelope, tel)
      })
    },
    dialect: 'sqlite',
    concurrentTransactions: !!options.replica,
    setSessionUser(userId: string | null) {
      return runAfterTransactions(async () => {
        const client = await getClient()
        unwrap(await client.request<WorkerCallEnvelope<void>>({ op: 'setSessionUser', sessionId, args: [userId] }), defaultTel)
      })
    },
    resetReplica(identity: Uint8Array, ctx?: DriverCallContext) {
      const tel = ctx?.tel ?? defaultTel
      return runAfterTransactions(async () => {
        const client = await getClient()
        return unwrap(await client.request<WorkerCallEnvelope<bigint>>({ op: 'resetReplica', sessionId, args: [identity, callBaggage(tel)] }), tel)
      })
    },
    async exchange(ctx?: DriverCallContext & { signal?: AbortSignal }) {
      const tel = ctx?.tel ?? defaultTel
      const id = crypto.randomUUID()
      const client = await getClient()
      if (ctx?.signal?.aborted) throw ctx.signal.reason
      const abort = () => { void client.request({ op: 'cancelExchange', sessionId, args: [id] }) }
      ctx?.signal?.addEventListener('abort', abort, { once: true })
      try { return unwrap(await client.request<WorkerCallEnvelope<bigint>>({ op: 'exchange', sessionId, args: [callBaggage(tel), id] }), tel) }
      finally { ctx?.signal?.removeEventListener('abort', abort) }
    },
    ownership,
    bootMetrics,
    async subscribeReplica(listener) {
      const client = await getClient()
      return client.subscribeReplica(listener)
    },
    subscribe(sql, onInvalidate) {
      return runAfterTransactions(async () => {
        const client = await getClient()
        return client.subscribe(sql, onInvalidate)
      })
    },
    query<T>(
      sql: string,
      params?: unknown[],
      ctx?: TursoDriverCallContext,
    ): Promise<T[]> {
      const tel = ctx?.tel ?? defaultTel
      return runAfterTransactions(() =>
        getClient().then((client) =>
          client
            .request<WorkerCallEnvelope<T[]>>({
              args: [sql, params, callBaggage(tel)],
              op: 'query',
              sessionId,
            })
            .then((envelope) => unwrap(envelope, tel)),
        ),
      )
    },
    exec(
      sql: string,
      params?: unknown[],
      ctx?: TursoDriverCallContext,
    ): Promise<void> {
      const tel = ctx?.tel ?? defaultTel
      return runAfterTransactions(async () => {
        const client = await getClient()
        const envelope = await client.request<WorkerCallEnvelope<void>>({
          args: [sql, params, callBaggage(tel)],
          op: 'exec',
          sessionId,
        })
        unwrap(envelope, tel)
      })
    },
    copyFrom(): Promise<void> {
      return Promise.reject(new Error('Turso driver does not support copyFrom'))
    },
    transaction<T>(
      fn: (txArg: DatabaseDriver) => Promise<T>,
      ctx?: TursoDriverCallContext,
    ): Promise<T> {
      return runTransactionAttempts(() => runAfterTransactions(() => runRootTransactionNow(fn, ctx), true), !!options.replica && !ctx?.transactionRetryManaged)
    },
    async close(): Promise<void> {
      // Retire the session before awaiting subscription cleanup; even an empty
      // async cleanup otherwise lets the queued attachment start after close.
      const hadFailureSubscriptions = sharedClient.failureEvents.size > 0
      const releaseFailures = failures.close()
      const closeClient = hadFailureSubscriptions
        ? releaseFailures.then(() => sharedClient.close(), () => sharedClient.close())
        : sharedClient.close()
      await disposeAll([
        () => releaseFailures,
        () => closeClient,
        () => opening?.then(() => null, () => null),
      ], 'Shared Turso driver cleanup failed')
    },

    runWorkerTask<TOutput>(
      moduleUrl: string,
      input: unknown,
      ctx?: TursoDriverCallContext,
    ): Promise<TOutput> {
      const tel = ctx?.tel ?? defaultTel
      return runAfterTransactions(() => getClient().then((client) =>
        client
          .request<WorkerCallEnvelope<TOutput>>({
            args: [moduleUrl, input, callBaggage(tel)],
            op: 'runTask',
            sessionId,
          })
          .then((envelope) => unwrap(envelope, tel)),
      ))
    },
  }
}


export async function runHostRpcRequest(
  request: HostRpcRequest,
  hostDrivers: Map<string, LocalHostDriver>,
  hostSessions: Map<string, string>,
  options: ResolvedTursoWorkerDriverOptions,
  acquireHost: (path: string) => Promise<() => Promise<void>>,
  port: MessagePort,
  onWorkerFailure: (path: string, error: Error) => void,
): Promise<unknown> {
  if (request.op === 'deleteByPrefix') {
    const prefix = request.args?.[0]
    if (typeof prefix !== 'string')
      throw new TypeError('Turso deleteByPrefix requires prefix')

    for (const [path, host] of hostDrivers) {
      if (!path.startsWith(prefix)) continue
      if (path === options.path) options.onOwnershipChange?.(false)
      hostDrivers.delete(path)
      try {
        await host.driver.close?.()
      } finally {
        await host.release()
      }
    }
    for (const [sessionId, path] of hostSessions) {
      if (path.startsWith(prefix)) hostSessions.delete(sessionId)
    }

    const worker = new Worker(new URL('./turso-worker.ts', import.meta.url), {
      type: 'module',
      name: `clear-${prefix}`,
    })
    const remote = Comlink.wrap<TursoWorkerDriver>(worker)
    const bootFailure = watchTursoWorkerBootFailure(worker, `clear-${prefix}`)
    try {
      await raceTursoWorkerBootFailure(
        remote.deleteDatabasesByPrefix(prefix),
        bootFailure,
      )
    } finally {
      bootFailure.dispose()
      worker.terminate()
    }
    return null
  }

  if (request.op === 'open') {
    if (!request.path) throw new Error('Turso host open missing path')
    const path = request.path
    let host = hostDrivers.get(request.path)
    if (host) {
      if (host.replicaKey !== `${Boolean(request.injected)}:${replicaTransportKey(request.replica)}`) throw new Error('Database already opened with different replication configuration')
    } else {
      await using startup = new AsyncDisposableStack()
      const release = await acquireHost(request.path)
      startup.defer(release)
      logTursoSharedClient(
        'host open',
        request.path,
        request.migrations?.length ?? 0,
      )
      const subscriptions = new Map<string, Map<number, number>>()
      const invalidationTarget = { port }
      hostSessions.set(request.sessionId, path)
      startup.defer(() => { hostSessions.delete(request.sessionId) })
      host = {
        replicaKey: `${Boolean(request.injected)}:${replicaTransportKey(request.replica)}`,
        failureSubscriptions: new Map(),
        release,
        subscriptions,
        invalidationTarget,
        driver: createLocalTursoWorkerDriver({
          tel: options.tel,
          onReplicaEvent(event) {
            if (hostDrivers.get(path) !== host) return
            for (const [sessionId, sessionPath] of hostSessions) {
              if (sessionPath === path) invalidationTarget.port.postMessage({ type: 'replica-event', sessionId, event }, [])
            }
          },
          onInvalidated(ids) {
            for (const [sessionId, live] of subscriptions) {
              const owned = subscriptionIds(live, ids)
              if (owned.length > 0) invalidationTarget.port.postMessage({ type: 'invalidated', sessionId, ids: owned }, [])
            }
          },
          createWorker: options.createWorker,
          env: options.env,
          replica: copyReplicaTransport(request.replica),
          replicaTransport: options.replicaTransport,
          capture: request.capture,
          migrations: request.migrations ?? [],
          path,
        }, error => { onWorkerFailure(path, error) }),
      }
      const startingHost = host
      startup.defer(async () => {
        try {
          await startingHost.driver.close?.()
        } finally {
          if (hostDrivers.get(path) === startingHost) hostDrivers.delete(path)
        }
      })
      hostDrivers.set(request.path, host)
      await host.driver.query('SELECT 1')
      startup.move()
    }
    const live = host.subscriptions.get(request.sessionId) ?? new Map<number, number>()
    host.subscriptions.set(request.sessionId, live)
    hostSessions.set(request.sessionId, request.path)
    const state = host.driver._replicaState
    if (state) port.postMessage({ type: 'replica-event', sessionId: request.sessionId, event: {
      state: { status: state.status, cursor: state.cursor, identity: state.identity, initialReplayComplete: state.initialReplayComplete, syncError: state.syncError, updateRequired: state.updateRequired, ...(state.error === undefined ? {} : { error: serializeError(state.error) }) },
    } }, [])
    if (request.path === options.path) options.onOwnershipChange?.(true)
    logTursoSharedClient('host ready', request.path)
    const metrics = await host.driver.bootMetrics
    return metrics ?? null
  }

  const path = hostSessions.get(request.sessionId)
  if (!path && request.op === 'close') return null
  if (!path)
    throw new Error(`Turso host session not found: ${request.sessionId}`)
  const host = hostDrivers.get(path)
  if (!host) throw new Error(`Turso host path not found: ${path}`)

  if (request.op === 'close') {
    if (host.txSessionId === request.sessionId) {
      await host.driver._callWorker('rollbackTx', [])
      host.txSessionId = undefined
    }
    for (const id of host.failureSubscriptions.get(request.sessionId) ?? []) await host.driver._callWorker('unsubscribeFailures', [id])
    host.failureSubscriptions.delete(request.sessionId)
    const errors: unknown[] = []
    for (const id of host.subscriptions.get(request.sessionId)?.values() ?? []) {
      try { await host.driver.unsubscribeId(id) } catch (error) { errors.push(error) }
    }
    host.subscriptions.delete(request.sessionId)
    if (![...hostSessions].some(([id, sessionPath]) => id !== request.sessionId && sessionPath === path)) {
      try { await host.driver.close?.() } catch (error) { errors.push(error) }
      hostDrivers.delete(path)
      try { await host.release() } catch (error) { errors.push(error) }
      logTursoSharedClient('host close', path)
    }
    hostSessions.delete(request.sessionId)
    if (errors.length === 1) throw errors[0]
    if (errors.length > 0) throw new AggregateError(errors, 'Failed to close Turso host session')
    return null
  }

  return executeHostRequest(request, host, port)
}

async function executeHostRequest(request: HostRpcRequest, host: LocalHostDriver, port: MessagePort): Promise<unknown> {
  if (request.op === 'subscribe') {
    const sql = request.args?.[0] as string
    const ids = host.subscriptions.get(request.sessionId) ?? new Map<number, number>()
    if (host.txSessionId) {
      throw new Error('Turso subscribe must execute outside the active transaction')
    }
    const registration = await host.driver.subscribeIds(sql)
    ids.set(request.requestId, registration)
    host.subscriptions.set(request.sessionId, ids)
    return request.requestId
  }
  if (request.op === 'unsubscribe') {
    const id = request.args?.[0] as number
    const ids = host.subscriptions.get(request.sessionId)
    const registration = ids?.get(id)
    if (registration !== undefined) { ids?.delete(id); await host.driver.unsubscribeId(registration) }
    return null
  }
  if (request.op === 'open' || request.op === 'close' || request.op === 'deleteByPrefix') {
    throw new Error('inv:1 lifecycle operations must not enter SQL execution')
  }
  const result = await host.driver._callWorker(request.op, request.args ?? [])
  if (request.op === 'subscribeFailures' && (result as WorkerCallEnvelope<void>).ok) {
    const ids = host.failureSubscriptions.get(request.sessionId) ?? new Set<string>()
    ids.add(request.args?.[1] as string)
    host.failureSubscriptions.set(request.sessionId, ids)
  }
  if (request.op === 'unsubscribeFailures') host.failureSubscriptions.get(request.sessionId)?.delete(request.args?.[0] as string)
  if (request.op === 'cancelExchange') return result
  const envelope = result as WorkerCallEnvelope<unknown>
  if (request.op === 'beginTx') {
    if (envelope.ok) host.txSessionId = request.sessionId
    return result
  }
  if ((request.op === 'commitTx' && envelope.ok) || request.op === 'rollbackTx') host.txSessionId = undefined
  for (const [sessionId, live] of host.subscriptions) {
    if (sessionId === request.sessionId) continue
    const ids = subscriptionIds(live, envelope.invalidated)
    if (ids.length > 0) port.postMessage({ type: 'invalidated', sessionId, ids })
  }
  const callingIds = host.subscriptions.get(request.sessionId)
  return { ...envelope, invalidated: subscriptionIds(callingIds, envelope.invalidated) }
}

function subscriptionIds(live: Map<number, number> | undefined, engineIds: number[]): number[] {
  return [...live ?? []].filter(([, registration]) => engineIds.includes(registration)).map(([id]) => id)
}

function createSharedDriverRef(
  path: string,
  shared: SharedTursoWorkerDriver,
  defaultTel: Tel,
): TursoWorkerDatabaseDriver {
  let closed = false
  let closing: Promise<void> | undefined
  const subscriptions = new Set<() => void>()
  function assertNotDestroyed(): void {
    if (shared.destroyed)
      throw new Error(`Turso worker database destroyed: ${path}`)
    if (closed) throw new Error(`Turso worker database closed: ${path}`)
  }

  const failureEvents = new Set<(event: FailureEvent) => void>()
  const failureReleases = new Map<string, () => void>()
  const failures = createFailureClient(
    request => { assertNotDestroyed(); if (!shared.driver.failures) throw new Error('Database has no native failure commands'); return executeFailureCommand(shared.driver.failures, request) },
    async (scope, id) => {
      assertNotDestroyed()
      if (!shared.driver.failures) throw new Error('Database has no native failure commands')
      const release = await shared.driver.failures.subscribe(scope, () => { for (const notify of failureEvents) notify({ id }) }, error => { for (const notify of failureEvents) notify({ id, error: serializeError(error) }) })
      if (closed || shared.destroyed) { release(); assertNotDestroyed() }
      failureReleases.set(id, release)
    },
    id => { failureReleases.get(id)?.(); failureReleases.delete(id); return Promise.resolve() },
    failureEvents,
  )
  return {
    failures,
    sourceTransaction: (scope, statements, context) => {
      assertNotDestroyed()
      if (!shared.driver.sourceTransaction) throw new Error('Database has no native source transaction')
      return shared.driver.sourceTransaction(scope, statements, context)
    },
    get concurrentTransactions() { return shared.driver.concurrentTransactions },
    get dialect() {
      return shared.driver.dialect
    },
    async subscribe(sql, onInvalidate) {
      assertNotDestroyed()
      const release = await shared.driver.subscribe(sql, () => {
        if (!closed && !shared.destroyed) onInvalidate()
      })
      if (closed || shared.destroyed) {
        release()
        assertNotDestroyed()
      }
      const unsubscribe = (): void => {
        if (!subscriptions.delete(unsubscribe)) return
        release()
      }
      subscriptions.add(unsubscribe)
      return unsubscribe
    },
    exchange(ctx?: DriverCallContext & { signal?: AbortSignal }) {
      assertNotDestroyed()
      if (!shared.driver.exchange) return Promise.reject(new Error('Turso driver missing native replica'))
      return shared.driver.exchange({ tel: ctx?.tel ?? defaultTel,signal: ctx?.signal })
    },
    setSessionUser(userId: string | null) {
      assertNotDestroyed()
      if (!shared.driver.setSessionUser) return Promise.reject(new Error('Turso driver lacks session-user support'))
      return shared.driver.setSessionUser(userId)
    },
    resetReplica(identity: Uint8Array, ctx?: DriverCallContext) {
      assertNotDestroyed()
      if (!shared.driver.resetReplica) return Promise.reject(new Error('Turso driver missing native replica reset'))
      return shared.driver.resetReplica(identity, { tel: ctx?.tel ?? defaultTel })
    },
    ownership: shared.driver.ownership,
    async subscribeReplica(listener) {
      assertNotDestroyed()
      if (!shared.driver.subscribeReplica) throw new Error('Turso driver missing native replica')
      const release = await shared.driver.subscribeReplica(state => { if (!closed && !shared.destroyed) listener(state) })
      if (closed || shared.destroyed) { release(); assertNotDestroyed() }
      const unsubscribe = (): void => { if (subscriptions.delete(unsubscribe)) release() }
      subscriptions.add(unsubscribe)
      return unsubscribe
    },
    get bootMetrics() {
      return shared.driver.bootMetrics
    },
    query<T>(
      sql: string,
      params?: unknown[],
      ctx?: TursoDriverCallContext,
    ): Promise<T[]> {
      assertNotDestroyed()
      return shared.driver.query<T>(sql, params, {
        tel: ctx?.tel ?? defaultTel,
      })
    },
    exec(
      sql: string,
      params?: unknown[],
      ctx?: TursoDriverCallContext,
    ): Promise<void> {
      assertNotDestroyed()
      return shared.driver.exec(sql, params, { tel: ctx?.tel ?? defaultTel })
    },
    copyFrom(
      ...args: Parameters<DatabaseDriver['copyFrom']>
    ): ReturnType<DatabaseDriver['copyFrom']> {
      assertNotDestroyed()
      return shared.driver.copyFrom(...args)
    },
    transaction<T>(
      fn: (tx: DatabaseDriver) => Promise<T>,
      ctx?: TursoDriverCallContext,
    ): Promise<T> {
      assertNotDestroyed()
      return shared.driver.transaction(fn, { ...ctx, tel: ctx?.tel ?? defaultTel })
    },
    close(): Promise<void> {
      if (closing) return closing
      if (closed || shared.destroyed) return Promise.resolve()
      closed = true
      const hadFailureSubscriptions = failureEvents.size > 0
      const releaseFailures = failures.close()
      for (const unsubscribe of subscriptions) unsubscribe()
      shared.refs -= 1
      if (shared.refs > 0) return closing = releaseFailures
      if (sharedDrivers.get(path) === shared) sharedDrivers.delete(path)
      shared.destroyed = true
      shared.unsubscribeDestroy()
      const closePhysical = () => shared.driver.close?.() ?? Promise.resolve()
      const closeDriver = hadFailureSubscriptions ? releaseFailures.then(closePhysical, closePhysical) : closePhysical()
      closing = disposeAll([() => releaseFailures, () => closeDriver], 'Failure subscriptions and shared driver cleanup failed')
      return closing
    },
    runWorkerTask<TOutput>(
      moduleUrl: string,
      input: unknown,
      ctx?: TursoDriverCallContext,
    ): Promise<TOutput> {
      assertNotDestroyed()
      return shared.driver.runWorkerTask<TOutput>(moduleUrl, input, {
        tel: ctx?.tel ?? defaultTel,
      })
    },
  }
}

function swallowBootFailure(
  configurePromise: Promise<TursoWorkerBootMetrics | undefined>,
): Promise<TursoWorkerBootMetrics | undefined> {
  return configurePromise
    .catch(() => null)
    .then((metrics) => metrics ?? undefined)
}

function annotateTursoWorkerError(path: string, error: unknown): Error {
  if (error instanceof Error) {
    return new Error(
      `Failed to open Turso OPFS database "${path}": ${error.message}`,
      { cause: error },
    )
  }
  return new Error(
    `Failed to open Turso OPFS database "${path}": ${String(error)}`,
  )
}

export function logTursoSharedClient(...args: unknown[]): void {
  // Blocked site data throws from the localStorage getter: debug is then off.
  let flag: string | null | undefined
  try { flag = globalThis.localStorage?.getItem('tursoDebug') } catch { return }
  if (flag !== '1') return
  console.info('[turso-shared-client]', ...args)
}
